import React from 'react';

import { ConfigPropTypes } from "../../../../../ConfigPropTypes";
import { formatText } from "../../resumeFormattingUtils";
import ResumePaper from "../../ResumePaper";
import ResumeHeader from "./ResumeHeader";
import { useLanguage } from "../../../../../i18n";

const StandardResumeTemplate = ({ config }) => {
    const { t, language } = useLanguage();
    const resume = config.resumeData?.[language] || config.resumeData?.en || null;
    
    // Handle case where resume data is not available
    if (!resume) {
        return (
            <ResumePaper>
                <div className="p-8 text-center">
                    <p className="text-red-500">Resume data not available</p>
                </div>
            </ResumePaper>
        );
    }

    // Apply tighter spacing for languages with longer text (FR, PT)
    const isCompactLanguage = language === 'fr' || language === 'pt';
    const sectionSpacing = isCompactLanguage ? 'space-y-1' : 'space-y-2';
    const sidebarSpacing = isCompactLanguage ? 'space-y-4' : 'space-y-5';
    const skillsSpacing = isCompactLanguage ? 'space-y-2' : 'space-y-3';
    const experienceLineHeight = isCompactLanguage ? 'leading-snug' : 'leading-normal';
    const paperSpacing = isCompactLanguage ? 'space-y-2' : 'space-y-3';

    return (
        <>
            <ResumePaper isCompact={isCompactLanguage}>
                {/* Header */}
                <ResumeHeader config={config} resume={resume} isCompact={isCompactLanguage}></ResumeHeader>

                {/* Main Content */}
                <main className={`flex ${paperSpacing}`}>
                    {/* Relevant Experience */}
                    <section className={`w-full ${sectionSpacing} mr-10`}>
                        <h3 className="font-bold text-light-800 text-lg"> { resume.experiences.length > 1 ? t('resume.relevantExperience') : t('resume.lastExperience') }</h3>
                        {resume.experiences.map((experience, index) => (
                            index < 2 ? (<div key={index} className={sectionSpacing}>
                                {experience.hideHeader ? null : (
                                    <h4 className="text-sm flex items-start leading-tight justify-between gap-2">
                                        <div className="flex-1"><span className="font-bold">{experience.description}</span> <span className="font-bold">• {experience.company}</span></div>
                                        <p className="font-raleway text-gray-500 text-[13px] whitespace-nowrap shrink-0">{experience.startDate} - {experience.endDate}</p>
                                    </h4>
                                )}
                                    <div className={`text-[.75rem] justify-between ${experienceLineHeight}`}>
                                        {formatText(experience.details)}
                                    </div>
                                </div>
                            ):null))}
                    </section>

                    {/* Skills, Education, Languages */}
                    <div className={`w-80 !border-success-700 ${sidebarSpacing}`}>
                        <section className={skillsSpacing}>
                            <h3 className="font-bold text-light-800 text-lg">{t('resume.skills')}</h3>
                            {resume.skills.map((skill, index)=>(
                                <div key={index} className="space-y-0">
                                    <h4 className="text-[.75rem] flex items-center leading-none justify-between">
                                        <span className="font-bold">{skill.area}</span>
                                    </h4>
                                    <div className="leading-tight">
                                        {skill.skills.map((areaSkill, skillIndex)=>(
                                            <span key={skillIndex} className="text-[.75rem]">
                                        {areaSkill}{skillIndex < skill.skills.length - 1 ? ', ' : '.'}
                                    </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </section>
                        {/* Education */}
                        <section className={skillsSpacing}>
                            <h3 className="font-bold text-light-800 text-lg">{t('resume.education')}</h3>
                            {resume.education.map((education, index)=>(
                                <div key={index} className="space-y-1">
                                    <h4 className="text-[.75rem] flex items-center leading-none justify-between">
                                        <span className="font-bold">{education.title}</span>
                                    </h4>
                                    <div className="text-[.75rem] mt-0 space-y-0">
                                        {education.description}
                                    </div>
                                    {(education.startDate || education.endDate) && (
                                        <div className="text-[.75rem]">
                                            {education.startDate} {education.startDate && education.endDate ? '-' : ''} {education.endDate}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </section>
                        {/* Languages */}
                        <section className={skillsSpacing}>
                            <h3 className="font-bold text-light-800 text-lg">{t('resume.languages')}</h3>
                            {resume.languages.map((language, index)=>(
                                <div key={index} className="space-y-1">
                                    <h4 className="text-[.75rem] flex items-center leading-none justify-between">
                                        <span className="font-bold">{resume.languages[index].name}</span>
                                    </h4>
                                    <div className="text-[.75rem] mt-0 space-y-0">
                                        {resume.languages[index].proficiency}
                                    </div>
                                </div>
                            ))}
                        </section>
                    </div>
                </main>

                {/*/!* Footer / Selected Projects *!/*/}
                {/*<footer className="space-y-5">*/}
                {/*    <h3 className="font-bold text-lg"> Selected Projects </h3>*/}
                {/*    <div className="grid grid-cols-3 gap-5">*/}
                {/*        {config.resume.projects.map((project) => (*/}
                {/*            <div className="bg-gray-200 rounded-2xl px-3 space-y-1 p-2 print:bg-gray-200 print-bg-color">*/}
                {/*                <h4 className="font-bold text-sm">{project.name}</h4>*/}
                {/*                <div className="text-[.70rem]">{project.description}</div>*/}
                {/*            </div>*/}
                {/*        ))}*/}
                {/*    </div>*/}
                {/*</footer>*/}
            </ResumePaper>
            <ResumePaper isCompact={isCompactLanguage}>
                {/* Header */}
                <div className="print:hidden">
                    <ResumeHeader config={config} resume={resume} isCompact={isCompactLanguage}></ResumeHeader>
                </div>

                <main className={`flex ${paperSpacing}`}>

                    {/* Relevant Experience */}
                    <section className={`w-full ${sectionSpacing} mr-10`}>
                        <h3 className="font-bold text-light-800 text-lg"> { resume.experiences.length > 1 ? t('resume.relevantExperience') : t('resume.lastExperience') }</h3>
                        {resume.experiences.map((experience, index) => (
                            index >= 2 && index <= 3 ? (<div key={index} className={sectionSpacing}>
                                {experience.hideHeader ? null : (
                                    <h4 className="text-sm flex items-start leading-tight justify-between gap-2">
                                        <div className="flex-1"><span className="font-bold">{experience.description}</span> <span className="font-bold">• {experience.company}</span></div>
                                        <p className="font-raleway text-gray-500 text-[13px] whitespace-nowrap shrink-0">{experience.startDate} - {experience.endDate}</p>
                                    </h4>
                                )}
                                    <div className={`text-[.75rem] justify-between ${experienceLineHeight}`}>
                                        {formatText(experience.details)}
                                    </div>
                                </div>
                            ):null))}
                    </section>
                </main>

            </ResumePaper>
            <ResumePaper isCompact={isCompactLanguage}>
                {/* Header */}
                <div className="print:hidden">
                    <ResumeHeader config={config} resume={resume} isCompact={isCompactLanguage}></ResumeHeader>
                </div>

                <main className={`flex ${paperSpacing}`}>

                    {/* Relevant Experience */}
                    <section className={`w-full ${sectionSpacing} mr-10`}>
                        <h3 className="font-bold text-light-800 text-lg"> { resume.experiences.length > 1 ? t('resume.relevantExperience') : t('resume.lastExperience') }</h3>
                        {resume.experiences.map((experience, index) => (
                            index > 3 ? (<div key={index} className={sectionSpacing}>
                                {experience.hideHeader ? null : (
                                    <h4 className="text-sm flex items-start leading-tight justify-between gap-2">
                                        <div className="flex-1"><span className="font-bold">{experience.description}</span> <span className="font-bold">• {experience.company}</span></div>
                                        <p className="font-raleway text-gray-500 text-[13px] whitespace-nowrap shrink-0">{experience.startDate} - {experience.endDate}</p>
                                    </h4>
                                )}
                                    <div className={`text-[.75rem] justify-between ${experienceLineHeight}`}>
                                        {formatText(experience.details)}
                                    </div>
                                </div>
                            ):null))}
                    </section>
                </main>

                {/* Footer / Selected Projects */}
                <footer className={skillsSpacing}>
                    <h3 className="font-bold text-lg"> {t('resume.selectedProjects')} </h3>
                    <div className="grid grid-cols-3 gap-3">
                        {resume.projects.map((project, index) => (
                            <div key={index} className="bg-gray-200 rounded-2xl px-3 space-y-1 p-2 print:bg-gray-200 print-bg-color">
                                <h4 className="font-bold text-[13px]">{project.name}</h4>
                                <div className="text-[.70rem] leading-tight">{project.description}</div>
                            </div>
                        ))}
                    </div>
                </footer>
            </ResumePaper>
        </>
    );
};

StandardResumeTemplate.propTypes = {
     config: ConfigPropTypes.isRequired // or any other type that fits your data structure
};

export default StandardResumeTemplate;